import type { UiKey } from '../i18n/dict';
import {
  CalendarDays,
  ChartColumn,
  CreditCard,
  Search,
  Settings,
  UserRound,
  type LucideIcon,
} from '../ui/icons';
import { ROLE_OF_ROUTE, businessIdOf, type AppRole, type Route, type RouteName } from './routes';

/**
 * Нижняя навигация. У клиента и у кабинета она РАЗНАЯ (ТЗ §14.3) — отсюда таблица по роли,
 * а не один общий список с флагами видимости.
 */
export interface TabDef {
  /** Экран, который вкладка открывает и по которому подсвечивается. */
  readonly name: RouteName;
  readonly route: Route;
  /** Контурная иконка Lucide — см. `ui/icons.tsx`. */
  readonly icon: LucideIcon;
  readonly labelKey: UiKey;
}

/**
 * Вкладки клиента.
 *
 * «Профиль» появился 16 сентября 2026 вместе со сносом splash: язык и вход в кабинет
 * жили на нём, и без нового дома они исчезли бы вместе с экраном. «Избранного» с макета
 * здесь нет — таблицы избранного нет в схеме, а вкладка на пустой экран это тихий фолбэк.
 */
export const CLIENT_TABS: readonly TabDef[] = [
  { name: 'catalog', route: { name: 'catalog' }, icon: Search, labelKey: 'navCatalog' },
  { name: 'my', route: { name: 'my' }, icon: CalendarDays, labelKey: 'navMyBook' },
  { name: 'profile', route: { name: 'profile' }, icon: UserRound, labelKey: 'navProfile' },
];

/**
 * Вкладки кабинета бизнеса.
 *
 * Четыре, как в прототипе. «Статистика» (21 сентября 2026) показывает и рейтинг: оценки
 * клиентов появились в схеме (`ratings`), и отдельная вкладка ради одного числа не нужна.
 *
 * Вкладки требуют `businessId`, поэтому строятся функцией: организация выбирается на
 * `bizHome`, и до этого выбора нижней панели у кабинета нет.
 */
export function businessTabs(businessId: string): readonly TabDef[] {
  return [
    {
      name: 'calendar',
      route: { name: 'calendar', businessId },
      icon: CalendarDays,
      labelKey: 'navCalendar',
    },
    {
      name: 'bizStats',
      route: { name: 'bizStats', businessId },
      icon: ChartColumn,
      labelKey: 'navStats',
    },
    {
      name: 'bizSettings',
      route: { name: 'bizSettings', businessId },
      icon: Settings,
      labelKey: 'navSettings',
    },
    { name: 'bizPlan', route: { name: 'bizPlan', businessId }, icon: CreditCard, labelKey: 'navPlan' },
  ];
}

/**
 * Статический список для `TABS_BY_ROLE`. Пуст: набор вкладок кабинета зависит от открытой
 * организации, и подставить сюда «какую-нибудь» значило бы увести владельца в чужие настройки.
 * Реальный набор берёт `tabsFor` ниже.
 */
export const BUSINESS_TABS: readonly TabDef[] = [];

export const TABS_BY_ROLE: Readonly<Record<AppRole, readonly TabDef[]>> = {
  client: CLIENT_TABS,
  business: BUSINESS_TABS,
};

/**
 * Набор вкладок для открытого маршрута.
 *
 * У клиента он постоянный, у кабинета — зависит от организации в маршруте. Когда её нет
 * (список организаций, онбординг), вкладок нет вовсе: панель, ведущая в календарь
 * несуществующей организации, показала бы ошибку вместо экрана.
 */
export function tabsFor(route: Route): readonly TabDef[] {
  if (ROLE_OF_ROUTE[route.name] === 'client') return CLIENT_TABS;
  const businessId = businessIdOf(route);
  return businessId === null ? [] : businessTabs(businessId);
}
